import { PatternType, WeaponType, Difficulty } from '../types';
import {
  LevelConfig,
  LevelCategory,
  LEVELS,
  getLevelById,
  isLevelUnlocked,
  getLevelsByCategoryForUI,
} from './LevelSystem';
import type { BossLevelConfig } from './LevelSystem';

/**
 * LevelManager - Handles manual level selection. All levels are unlocked by default.
 */
export class LevelManager {
  private currentLevel: LevelConfig;
  private completedLevels: Set<string> = new Set();
  private readonly STORAGE_KEY = 'dotdodger-completed-levels';

  constructor() {
    this.currentLevel = LEVELS[0];
    this.loadCompletedLevels();
  }

  private loadCompletedLevels(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          this.completedLevels = new Set(parsed);
        }
      }
    } catch (e) {
      console.warn('Failed to load completed levels:', e);
    }
  }

  private saveCompletedLevels(): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify([...this.completedLevels]));
    } catch (e) {
      console.warn('Failed to save completed levels:', e);
    }
  }

  selectLevel(levelId: string): boolean {
    const level = getLevelById(levelId);
    if (!level) {
      console.warn(`Level not found: ${levelId}`);
      return false;
    }
    // All levels unlocked - skip the locked check
    this.currentLevel = level;
    return true;
  }

  completeLevel(): void {
    this.completedLevels.add(this.currentLevel.id);
    this.saveCompletedLevels();
  }

  isLevelUnlocked(_levelId: string): boolean {
    return true;
  }

  isLevelCompleted(levelId: string): boolean {
    return this.completedLevels.has(levelId);
  }

  getCompletedLevels(): string[] {
    return [...this.completedLevels];
  }

  getCurrentLevel(): LevelConfig {
    return this.currentLevel;
  }

  getCurrentLevelId(): string {
    return this.currentLevel.id;
  }

  getScoreMultiplier(): number {
    return this.currentLevel.modifiers?.scoreMultiplier ?? 1.0;
  }

  isNoWeapons(): boolean {
    return this.currentLevel.modifiers?.noWeapons ?? false;
  }

  getSingleWeapon(): WeaponType | null {
    return this.currentLevel.modifiers?.singleWeapon ?? null;
  }

  /**
   * Get the boss configuration for the current level (or null if no bosses)
   */
  getBossConfig(): BossLevelConfig | null {
    return this.currentLevel.boss ?? null;
  }

  selectPattern(excludeTypes: PatternType[]): PatternType | null {
    const availablePatterns = this.currentLevel.patterns.filter(
      p => !excludeTypes.includes(p.type)
    );

    if (availablePatterns.length === 0) {
      return null;
    }

    const weights = availablePatterns.map((_, index) => {
      return 1 + (index / availablePatterns.length) * 0.5;
    });

    const totalWeight = weights.reduce((sum, w) => sum + w, 0);
    let random = Math.random() * totalWeight;

    for (let i = 0; i < availablePatterns.length; i++) {
      random -= weights[i];
      if (random <= 0) {
        return availablePatterns[i].type;
      }
    }

    return availablePatterns[availablePatterns.length - 1].type;
  }

  getPatternDifficulty(patternType: PatternType): Difficulty {
    const patternConfig = this.currentLevel.patterns.find(
      p => p.type === patternType
    );
    return patternConfig?.difficulty ?? Difficulty.MEDIUM;
  }

  selectWeapon(): WeaponType | null {
    if (this.isNoWeapons()) {
      return null;
    }

    const singleWeapon = this.getSingleWeapon();
    if (singleWeapon) {
      return singleWeapon;
    }

    const availableWeapons = this.currentLevel.weapons;
    if (availableWeapons.length === 0) {
      return null;
    }

    const randomIndex = Math.floor(Math.random() * availableWeapons.length);
    return availableWeapons[randomIndex].type;
  }

  isWeaponAvailable(weaponType: WeaponType): boolean {
    return this.currentLevel.weapons.some(w => w.type === weaponType);
  }

  getPatternInterval(): number {
    const { base, min } = this.currentLevel.patternInterval;
    const range = base - min;
    const randomOffset = Math.random() * range;
    return base - randomOffset;
  }

  getWeaponOrbCount(): number {
    return this.currentLevel.weaponOrbCount;
  }

  getThemeColor(): string {
    return this.currentLevel.themeColor;
  }

  getLevelName(): string {
    return this.currentLevel.name;
  }

  getLevelEmoji(): string {
    return this.currentLevel.iconEmoji;
  }

  getLevelsForUI(): Record<LevelCategory, LevelConfig[]> {
    return getLevelsByCategoryForUI();
  }

  static getLevelById(id: string): LevelConfig | null {
    return getLevelById(id);
  }

  resetProgress(): void {
    this.completedLevels.clear();
    this.saveCompletedLevels();
  }
}

// Re-export to keep isLevelUnlocked referenced (it's now a no-op but kept for API stability)
void isLevelUnlocked;
